/**
 * CostInsightsPanel
 *
 * AI-powered cost analysis panel for the Cost tab.
 *
 * Current: driven by mock data that simulates a real LLM response.
 * Production: replace the mock fetch in useCostInsights() with a real call to
 *   GET /data/apps/{appId}/cost/insights?environment=...&range=...
 * which sends the cost breakdown to an LLM and returns structured insights.
 *
 * Expected API response shape:
 * {
 *   summary: string,
 *   insights: [{ type, title, body, severity }],
 *   recommendations: [{ type, action_label, span_name, impact, rationale }]
 * }
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Sparkles, ArrowRight, Zap, TrendingDown, AlertTriangle } from 'lucide-react';
import { palette, components, typography, withOpacity } from '../../utils/designTokens';

// ─────────────────────────────────────────────────────────────────────────────
// Mock LLM response — replace useCostInsights() fetch with real API call
// ─────────────────────────────────────────────────────────────────────────────
const MOCK_INSIGHTS_RESPONSE = {
  summary: 'gpt-4o is driving 89% of your LLM spend. Three workflows account for 94% of total cost — the RAG Pipeline alone is 3× more expensive per trace than your other workflows.',
  insights: [
    {
      type: 'cost_concentration',
      severity: 'high',
      title: 'Model cost concentration',
      body: 'gpt-4o accounts for $4.21 (89%) of total LLM spend over the selected period. Your retrieval and validation spans use the same model as your core reasoning spans — most of these calls don\'t require GPT-4o-level capability.',
    },
    {
      type: 'workflow_driver',
      severity: 'medium',
      title: 'RAG Pipeline is your top cost business task',
      body: 'The RAG Pipeline business task costs $0.025 avg per trace — 3× higher than Customer Support at $0.008. The ChatCompletion span within it accounts for 92% of per-trace cost, averaging 2,847 input tokens per call.',
    },
    {
      type: 'token_efficiency',
      severity: 'low',
      title: 'Output-to-input token ratio is below typical',
      body: 'Your avg output/input ratio is 0.14 — below the 0.25 typical for RAG tasks. This may indicate system prompts are over-specified, generating large inputs that don\'t produce proportionally richer outputs.',
    },
  ],
  // Recommendations mirror MOCK_RECOMMENDATIONS['wf-rag-001'] in SimulationStudio.js
  // to ensure consistent data across the Cost tab and Simulation Studio
  recommendations: [
    {
      type: 'cost',
      action_label: 'gpt-4o → mini',
      span_name: 'ChatCompletion',
      impact: { cost: -68, latency: -22, quality: -3 },
      rationale: 'ChatCompletion accounts for 92% of cost. gpt-4o-mini has comparable answer_relevance (0.74 avg) at a fraction of the price.',
    },
    {
      type: 'latency',
      action_label: 'top_k 10 → 5',
      span_name: 'retrieve_context',
      impact: { cost: 0, latency: -38, quality: 0 },
      rationale: 'Context relevance shows diminishing returns after top 5 documents. Reduces retrieval time with no quality impact.',
    },
    {
      type: 'quality',
      action_label: 'tighten system prompt',
      span_name: 'ChatCompletion',
      impact: { cost: 0, latency: 0, quality: 14 },
      rationale: '3 hallucination signals detected on ChatCompletion in the last 7 days. Explicit grounding instructions improve faithfulness.',
    },
    {
      type: 'cost',
      action_label: 'temp 0.7 → 0.2',
      span_name: 'ChatCompletion',
      impact: { cost: -8, latency: -5, quality: 6 },
      rationale: 'Lower temperature reduces token variance in RAG tasks, cutting output tokens and improving faithfulness consistency.',
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Hook — swap setTimeout for real fetch when backend is ready
// ─────────────────────────────────────────────────────────────────────────────
function useCostInsights(appId, environment, range) {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    if (!appId) return;
    setState({ loading: true, data: null, error: null });

    // TODO: replace with real fetch:
    // fetch(`${API_URL}/data/apps/${appId}/cost/insights?environment=${environment}&range=${range}`, { headers })
    //   .then(r => r.json()).then(data => setState({ loading: false, data, error: null }))
    //   .catch(err => setState({ loading: false, data: null, error: err.message }));

    const timer = setTimeout(() => {
      setState({ loading: false, data: MOCK_INSIGHTS_RESPONSE, error: null });
    }, 900);

    return () => clearTimeout(timer);
  }, [appId, environment, range]);

  return state;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────
const SEVERITY_CONFIG = {
  high:   {
    color: palette.status.danger,
    bg: withOpacity(palette.status.danger, 7),
    borderColor: withOpacity(palette.status.danger, 25),
    icon: <AlertTriangle size={16} />,
    label: 'High Impact'
  },
  medium: {
    color: palette.accents.warm.base,
    bg: withOpacity(palette.accents.warm.base, 7),
    borderColor: withOpacity(palette.accents.warm.base, 25),
    icon: <TrendingDown size={16} />,
    label: 'Medium Impact'
  },
  low:    {
    color: palette.accents.secondary.base,
    bg: withOpacity(palette.accents.secondary.base, 7),
    borderColor: withOpacity(palette.accents.secondary.base, 25),
    icon: <Zap size={16} />,
    label: 'Low Impact'
  },
};

const InsightCard = ({ insight }) => {
  const cfg = SEVERITY_CONFIG[insight.severity] || SEVERITY_CONFIG.low;

  // Convert body text into bullet points — split on '. ' that start new facts
  const bullets = insight.body
    .split(/(?<=\.)\s+(?=[A-Z])|(?<=\d)\s+(?=[A-Z])/)
    .map(s => s.trim())
    .filter(Boolean);

  return (
    <div
      className="flex flex-col gap-3 rounded-lg p-5"
      style={{
        backgroundColor: cfg.bg,
        border: `1px solid ${cfg.borderColor}`,
        flex: 1,
        minWidth: 0
      }}
    >
      {/* Header with icon and severity badge */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div
            className="flex items-center justify-center rounded-md"
            style={{
              color: cfg.color,
              backgroundColor: cfg.color + '18',
              width: 32,
              height: 32,
              flexShrink: 0
            }}
          >
            {cfg.icon}
          </div>
          <span className="text-sm font-semibold leading-tight" style={{ color: palette.text.primary }}>
            {insight.title}
          </span>
        </div>
        <span
          className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded"
          style={{
            color: cfg.color,
            backgroundColor: cfg.color + '20',
            flexShrink: 0
          }}
        >
          {cfg.label}
        </span>
      </div>

      {/* Body bullets */}
      <ul className="flex flex-col gap-2">
        {bullets.map((bullet, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <span
              style={{
                color: cfg.color,
                marginTop: 6,
                flexShrink: 0,
                width: 5,
                height: 5,
                borderRadius: '50%',
                display: 'inline-block',
                backgroundColor: cfg.color
              }}
            />
            <span className="text-sm leading-relaxed" style={{ color: palette.text.primary }}>
              {bullet}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
const CostInsightsPanel = ({ appId, environment, range }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading, data, error } = useCostInsights(appId, environment, range);

  // Navigate to Simulation Studio with the RAG Pipeline pre-selected
  // workflowId matches the high-cost workflow surfaced by the AI analysis
  const goToSimulationStudio = (workflowId = 'wf-rag-001') => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', 'simulation');
    params.set('workflowId', workflowId);
    navigate(`/app/${encodeURIComponent(appId)}?${params.toString()}`);
  };

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ border: `1px solid ${palette.borders.medium}`, marginBottom: 16 }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-6 py-3"
        style={{ borderBottom: `1px solid ${palette.borders.subtle}`, background: palette.neutrals.surfaceMid }}
      >
        <div className="flex items-center gap-2.5">
          <Sparkles size={13} style={{ color: palette.accents.primary.base, opacity: 0.9 }} />
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: palette.text.primary }}>
            AI Cost Analysis
          </span>
          {loading && (
            <span className="text-xs" style={{ color: palette.text.muted }}>Analysing…</span>
          )}
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center gap-3 px-6 py-5">
          <div
            style={{
              width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
              border: `2px solid ${palette.accents.primary.base}30`,
              borderTopColor: palette.accents.primary.base,
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <span className="text-xs" style={{ color: palette.text.muted }}>
            Analysing cost patterns across models, agents and workflows…
          </span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="px-5 py-4">
          <span className="text-xs" style={{ color: palette.status.danger }}>Unable to generate insights: {error}</span>
        </div>
      )}

      {/* Content */}
      {data && (
        <div className="flex flex-col gap-0">
          {/* Summary with highlighted metrics */}
          <div className="px-6 py-5" style={{ borderBottom: `1px solid ${palette.borders.subtle}` }}>
            <div className="flex items-center gap-6">
              {/* Metric 1 */}
              <div className="flex items-center gap-2">
                <span
                  className="px-2.5 py-1 rounded-md font-bold text-base"
                  style={{
                    backgroundColor: withOpacity(palette.status.danger, 13),
                    color: palette.status.danger
                  }}
                >
                  89%
                </span>
                <span className="text-sm" style={{ color: palette.text.primary }}>
                  gpt-4o driving LLM spend
                </span>
              </div>

              <span style={{ color: palette.borders.strong }}>|</span>

              {/* Metric 2 */}
              <div className="flex items-center gap-2">
                <span
                  className="px-2.5 py-1 rounded-md font-bold text-base"
                  style={{
                    backgroundColor: withOpacity(palette.accents.warm.base, 13),
                    color: palette.accents.warm.base
                  }}
                >
                  94%
                </span>
                <span className="text-sm" style={{ color: palette.text.primary }}>
                  from 3 workflows
                </span>
              </div>

              <span style={{ color: palette.borders.strong }}>|</span>

              {/* Metric 3 */}
              <div className="flex items-center gap-2">
                <span
                  className="px-2.5 py-1 rounded-md font-bold text-base"
                  style={{
                    backgroundColor: withOpacity(palette.accents.warm.base, 13),
                    color: palette.accents.warm.base
                  }}
                >
                  3×
                </span>
                <span className="text-sm" style={{ color: palette.text.primary }}>
                  RAG Pipeline vs other workflows
                </span>
              </div>
            </div>
          </div>

          {/* Insights row */}
          <div className="flex gap-4 px-6 py-5" style={{ borderBottom: `1px solid ${palette.borders.subtle}` }}>
            {data.insights.map((insight, i) => (
              <InsightCard key={i} insight={insight} />
            ))}
          </div>

          {/* CTA */}
          <div className="flex items-center justify-between px-6 py-4">
            <p className="text-xs" style={{ color: palette.text.muted }}>
              {data.recommendations.length} optimization{data.recommendations.length !== 1 ? 's' : ''} identified — open Simulation Studio to review and run them.
            </p>
            <button
              onClick={() => goToSimulationStudio()}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg transition-all"
              style={{
                fontSize: 11, fontWeight: 600, flexShrink: 0,
                background: palette.accents.primary.base,
                color: palette.text.inverse,
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Simulate in Simulation Studio <ArrowRight size={11} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CostInsightsPanel;
